import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { getAdminSection } from '../../admin/adminSections';
import { listAdminCategories } from '../../services/adminCategoryService';
import {
  buildProductImageUrl,
  createAdminProduct,
  deleteAdminProduct,
  listAdminProducts,
  updateAdminProduct,
} from '../../services/adminProductService';

const initialFormValues = {
  category_id: '',
  name: '',
  description: '',
  price: '',
  mrp: '',
  weight: '',
  stock: '',
  is_best_selling: '0',
  status: '1',
};

function normalizeProduct(item) {
  return {
    product_id:
      item.product_id ??
      item.id ??
      item.productId ??
      '',
    category_id: item.category_id ?? item.categoryId ?? '',
    name: item.name ?? '',
    description: item.description ?? '',
    image: item.image ?? '',
    price: item.price ?? '',
    mrp: item.mrp ?? '',
    weight: item.weight ?? '',
    stock: item.stock ?? '',
    is_best_selling: String(item.is_best_selling ?? '0'),
    status: String(item.status ?? '1'),
  };
}

function normalizeCategory(item) {
  return {
    category_id: item.category_id ?? item.id ?? item.categoryId ?? '',
    name: item.name ?? '',
  };
}

export default function ProductsPage() {
  const section = getAdminSection('products');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formValues, setFormValues] = useState(initialFormValues);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageName, setSelectedImageName] = useState('');
  const [existingImageLabel, setExistingImageLabel] = useState('');

  const visibleColumns = useMemo(() => {
    const fields = [
      { key: 'category_id', label: 'Category ID' },
      { key: 'name', label: 'Name' },
      { key: 'price', label: 'Price' },
      { key: 'stock', label: 'Stock' },
      { key: 'is_best_selling', label: 'Best Seller' },
      { key: 'image', label: 'Image' },
    ];

    if (isMobile) {
      return fields.slice(0, 2);
    }

    if (isTablet) {
      return fields.slice(0, 3);
    }

    return fields;
  }, [isMobile, isTablet]);

  const headerMetrics = [
    { label: 'Total products', value: String(items.length) },
    { label: 'Available categories', value: String(categories.length) },
    {
      label: 'Best sellers flagged',
      value: String(items.filter((item) => String(item.is_best_selling) === '1').length),
    },
  ];

  const fetchProducts = async (showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }

    try {
      const response = await listAdminProducts();
      setItems(response.items.map(normalizeProduct));
      setSubmitError(null);
    } catch (error) {
      setSubmitError(error);
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await listAdminCategories();
      setCategories(response.items.map(normalizeCategory));
    } catch (error) {
      setSubmitError((current) => current || error);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const handleFieldChange = (field) => (event) => {
    setFormValues((current) => ({
      ...current,
      [field]: event.target.value,
    }));

    setFieldErrors((current) => ({
      ...current,
      [field]: '',
    }));

    setSubmitError(null);
    setSubmitSuccess('');
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedImage(file);
    setSelectedImageName(file ? file.name : '');
    setFieldErrors((current) => ({
      ...current,
      image: '',
    }));
    setSubmitError(null);
    setSubmitSuccess('');
  };

  const resetForm = () => {
    setFormValues(initialFormValues);
    setEditingId(null);
    setFieldErrors({});
    setSelectedImage(null);
    setSelectedImageName('');
    setExistingImageLabel('');
  };

  const validateForm = () => {
    const errors = {};

    if (!formValues.category_id) {
      errors.category_id = 'Please choose a category.';
    }

    if (!formValues.name.trim()) {
      errors.name = 'Product name is required.';
    }

    if (!formValues.description.trim()) {
      errors.description = 'Please enter a product description.';
    }

    if (!formValues.price.trim()) {
      errors.price = 'Please enter the selling price.';
    }

    if (!formValues.mrp.trim()) {
      errors.mrp = 'Please enter the MRP.';
    }

    if (!formValues.weight.trim()) {
      errors.weight = 'Please enter the product weight.';
    }

    if (!formValues.stock.trim()) {
      errors.stock = 'Please enter the available stock.';
    }

    if (!editingId && !selectedImage) {
      errors.image = 'Please choose a product image file.';
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setSubmitError({
        message: 'Please review the product form before submitting.',
      });
      return;
    }

    setIsSaving(true);
    setSubmitError(null);
    setSubmitSuccess('');
    setFieldErrors({});

    try {
      const payload = {
        ...formValues,
        image: selectedImage,
      };

      if (editingId) {
        const result = await updateAdminProduct({
          product_id: editingId,
          ...payload,
        });
        setSubmitSuccess(result?.message || 'Product updated successfully.');
      } else {
        const result = await createAdminProduct(payload);
        setSubmitSuccess(result?.message || 'Product created successfully.');
      }

      resetForm();
      await fetchProducts(false);
    } catch (error) {
      setSubmitError(error);
      setFieldErrors(error.validationErrors || {});
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.product_id);
    setFormValues({
      category_id: String(item.category_id || ''),
      name: item.name || '',
      description: item.description || '',
      price: item.price || '',
      mrp: item.mrp || '',
      weight: item.weight || '',
      stock: String(item.stock || ''),
      is_best_selling: String(item.is_best_selling ?? '0'),
      status: String(item.status ?? '1'),
    });
    setSelectedImage(null);
    setSelectedImageName('');
    setExistingImageLabel(item.image || '');
    setSubmitError(null);
    setSubmitSuccess('');
  };

  const handleDelete = async (item) => {
    setDeletingId(item.product_id);
    setSubmitError(null);
    setSubmitSuccess('');

    try {
      const result = await deleteAdminProduct(item.product_id);
      if (editingId === item.product_id) {
        resetForm();
      }
      setSubmitSuccess(result?.message || 'Product deleted successfully.');
      await fetchProducts(false);
    } catch (error) {
      setSubmitError(error);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Grid container spacing={2.5} className='admin-dashboard-metrics'>
        {headerMetrics.map((metric) => (
          <Grid key={metric.label} size={{ xs: 12, md: 4 }}>
            <Paper className='admin-dashboard-metricCard' elevation={0}>
              <Typography className='admin-dashboard-metricLabel'>
                {metric.label}
              </Typography>
              <Typography className='admin-dashboard-metricValue'>
                {metric.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, xl: 5 }}>
          <Paper className='admin-dashboard-featureCard admin-crud-formCard' elevation={0}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent='space-between'
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Box>
                <Typography className='admin-dashboard-cardKicker'>
                  {editingId ? 'Edit product' : 'Create product'}
                </Typography>
                <Typography className='admin-dashboard-cardTitle'>
                  {editingId ? 'Update product' : 'Add product'}
                </Typography>
              </Box>
              <Chip
                label={editingId ? 'Edit mode' : 'Live API'}
                className='admin-dashboard-statusChip'
              />
            </Stack>

            <Typography className='admin-dashboard-cardText'>
              Manage product records using the protected admin product APIs,
              including image upload through multipart form-data.
            </Typography>

            {submitError ? (
              <Alert severity='error' className='auth-alert'>
                {submitError.message}
              </Alert>
            ) : null}

            {submitSuccess ? (
              <Alert severity='success' className='auth-alert'>
                {submitSuccess}
              </Alert>
            ) : null}

            <Box component='form' onSubmit={handleSubmit} className='admin-crud-form'>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    select
                    label='Category'
                    value={formValues.category_id}
                    onChange={handleFieldChange('category_id')}
                    error={Boolean(fieldErrors.category_id)}
                    helperText={fieldErrors.category_id}
                  >
                    {categories.map((category) => (
                      <MenuItem
                        key={category.category_id}
                        value={String(category.category_id)}
                      >
                        {category.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label='Name'
                    value={formValues.name}
                    onChange={handleFieldChange('name')}
                    error={Boolean(fieldErrors.name)}
                    helperText={fieldErrors.name}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label='Description'
                    value={formValues.description}
                    onChange={handleFieldChange('description')}
                    error={Boolean(fieldErrors.description)}
                    helperText={fieldErrors.description}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='Price'
                    value={formValues.price}
                    onChange={handleFieldChange('price')}
                    error={Boolean(fieldErrors.price)}
                    helperText={fieldErrors.price}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='MRP'
                    value={formValues.mrp}
                    onChange={handleFieldChange('mrp')}
                    error={Boolean(fieldErrors.mrp)}
                    helperText={fieldErrors.mrp}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='Weight'
                    value={formValues.weight}
                    onChange={handleFieldChange('weight')}
                    error={Boolean(fieldErrors.weight)}
                    helperText={fieldErrors.weight}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label='Stock'
                    value={formValues.stock}
                    onChange={handleFieldChange('stock')}
                    error={Boolean(fieldErrors.stock)}
                    helperText={fieldErrors.stock}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label='Best Seller'
                    value={formValues.is_best_selling}
                    onChange={handleFieldChange('is_best_selling')}
                  >
                    <MenuItem value='1'>Yes</MenuItem>
                    <MenuItem value='0'>No</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label='Status'
                    value={formValues.status}
                    onChange={handleFieldChange('status')}
                  >
                    <MenuItem value='1'>Active</MenuItem>
                    <MenuItem value='0'>Inactive</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    component='label'
                    variant='outlined'
                    startIcon={<UploadFileRoundedIcon />}
                    className='admin-crud-secondaryButton'
                    sx={{ width: '100%', justifyContent: 'center' }}
                  >
                    {selectedImageName || 'Choose image file'}
                    <input type='file' accept='image/*' hidden onChange={handleImageChange} />
                  </Button>
                  {existingImageLabel && !selectedImageName ? (
                    <Typography className='admin-dashboard-note' sx={{ mt: 1 }}>
                      Current image: {existingImageLabel}
                    </Typography>
                  ) : null}
                  {fieldErrors.image ? (
                    <Typography color='error' sx={{ mt: 1, fontSize: '0.75rem' }}>
                      {fieldErrors.image}
                    </Typography>
                  ) : null}
                </Grid>
              </Grid>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                className='admin-crud-actions'
              >
                <Button
                  type='submit'
                  variant='contained'
                  startIcon={editingId ? <SaveRoundedIcon /> : <AddRoundedIcon />}
                  className='admin-crud-primaryButton'
                  disabled={isSaving}
                >
                  {isSaving
                    ? editingId
                      ? 'Updating...'
                      : 'Creating...'
                    : editingId
                      ? 'Update product'
                      : 'Add product'}
                </Button>
                <Button
                  type='button'
                  variant='outlined'
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={resetForm}
                  className='admin-crud-secondaryButton'
                >
                  Reset
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, xl: 7 }}>
          <Paper className='admin-dashboard-sideCard admin-crud-tableCard' elevation={0}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent='space-between'
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Box>
                <Typography className='admin-dashboard-cardKicker'>
                  Live records
                </Typography>
                <Typography className='admin-dashboard-cardTitle'>
                  {section.label} list
                </Typography>
              </Box>
              <Chip
                label={isLoading ? 'Loading...' : `${items.length} rows`}
                className='admin-dashboard-statusChip'
              />
            </Stack>

            <Typography className='admin-dashboard-note'>
              Products are loaded from the protected admin product endpoints.
              Update and delete route names were inferred to match the existing
              admin API naming style.
            </Typography>
            <Divider className='admin-dashboard-divider' />

            <TableContainer className='admin-crud-tableWrap'>
              <Table className='admin-crud-table'>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    {visibleColumns.map((column) => (
                      <TableCell key={column.key}>{column.label}</TableCell>
                    ))}
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length + 2}>
                        <Typography className='admin-dashboard-note'>
                          Loading products...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {!isLoading &&
                    items.map((item) => (
                      <TableRow key={item.product_id} hover>
                        <TableCell>{item.product_id}</TableCell>
                        {visibleColumns.map((column) => (
                          <TableCell key={column.key}>
                            {column.key === 'image' ? (
                              item.image ? (
                                <Box
                                  component='a'
                                  href={buildProductImageUrl(item.image)}
                                  target='_blank'
                                  rel='noreferrer'
                                  sx={{
                                    color: '#8d4314',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                  }}
                                >
                                  View image
                                </Box>
                              ) : (
                                '-'
                              )
                            ) : column.key === 'is_best_selling' ? (
                              String(item.is_best_selling) === '1' ? 'Yes' : 'No'
                            ) : (
                              item[column.key] || '-'
                            )}
                          </TableCell>
                        ))}
                        <TableCell align='right'>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            justifyContent='flex-end'
                            className='admin-crud-rowActions'
                          >
                            <Button
                              size='small'
                              variant='text'
                              startIcon={<EditRoundedIcon />}
                              onClick={() => handleEdit(item)}
                            >
                              Edit
                            </Button>
                            <Button
                              size='small'
                              color='error'
                              variant='text'
                              startIcon={<DeleteOutlineRoundedIcon />}
                              onClick={() => handleDelete(item)}
                              disabled={deletingId === item.product_id}
                            >
                              {deletingId === item.product_id ? 'Deleting...' : 'Delete'}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}

                  {!isLoading && items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length + 2}>
                        <Typography className='admin-dashboard-note'>
                          No products returned by the API yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {section.stats?.length ? (
        <Box className='admin-dashboard-pillGrid'>
          {section.stats.map((item) => (
            <Box key={item} className='admin-dashboard-pill'>
              {item}
            </Box>
          ))}
        </Box>
      ) : null}
    </>
  );
}
